import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

const getSocketUrl = (): string => {
    // 1. Explicit env var takes highest priority
    if (import.meta.env.VITE_API_URL) {
        return (import.meta.env.VITE_API_URL as string).replace(/\/api$/, '');
    }

    // 2. Same logic as api.ts getApiBaseUrl()
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');

    if (isLocal) {
        return 'http://localhost:5000';
    }

    // 3. Production — strip /api suffix from the known backend URL
    return 'https://backend-sable-five-58.vercel.app';
};

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef<Socket | null>(null);

    // Vercel serverless does NOT support persistent WebSocket connections.
    // Only attempt connection on localhost.
    const isProduction = typeof window !== 'undefined' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1' &&
        !window.location.hostname.startsWith('192.168.');

    const connectSocket = () => {
        if (!user) return;
        if (isProduction) {
            // WebSocket not supported on Vercel — skip silently
            return;
        }

        const token = localStorage.getItem('authToken');
        if (!token) return;

        // Disconnect existing socket if any
        if (socketRef.current) {
            socketRef.current.disconnect();
        }

        console.log('🔌 Connecting to WebSocket...');

        const socketUrl = getSocketUrl();
        const newSocket = io(socketUrl, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5
        });

        newSocket.on('connect', () => {
            console.log('✅ WebSocket connected');
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('❌ WebSocket disconnected:', reason);
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('❌ WebSocket connection error:', error);
            setIsConnected(false);
        });

        newSocket.on('reconnect', (attemptNumber) => {
            console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
            setIsConnected(true);
        });

        socketRef.current = newSocket;
        setSocket(newSocket);
    };

    const disconnectSocket = () => {
        if (socketRef.current) {
            console.log('🔌 Disconnecting WebSocket...');
            socketRef.current.disconnect();
            socketRef.current = null;
            setSocket(null);
            setIsConnected(false);
        }
    };

    const reconnect = () => {
        if (isProduction) return;
        disconnectSocket();
        setTimeout(connectSocket, 500);
    };

    // Connect when user logs in
    useEffect(() => {
        if (user) {
            connectSocket();
        } else {
            disconnectSocket();
        }

        return () => {
            disconnectSocket();
        };
    }, [user]);

    return (
        <WebSocketContext.Provider value={{ socket, isConnected, reconnect }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = (): WebSocketContextType => {
    const context = useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within WebSocketProvider');
    }
    return context;
};
